import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { pinnedReportsCollection, PinnedReport } from '@/collections/pinned_reports';

const MAX_PINNED_REPORTS = 10;

@Module({
  dynamic: true,
  store,
  name: 'pinnedReports',
  namespaced: true,
})
class PinnedReportsModule extends VuexModule {
  private pinnedReports: PinnedReport[] = [];

  get getPinnedReports(): PinnedReport[] {
    return this.pinnedReports;
  }

  get isReportPinned(): (id: number) => boolean {
    return (id: number) => this.pinnedReports.some((r) => r.id === id);
  }

  get pinnedReportCount(): number {
    return this.pinnedReports.length;
  }

  @Mutation
  SET_PINNED_REPORTS(items: PinnedReport[]): void {
    this.pinnedReports = items;
  }

  @Action
  clearPinnedReports(): void {
    this.context.commit('SET_PINNED_REPORTS', []);
  }

  @Action
  async loadPinnedReports(): Promise<void> {
    try {
      const items = await pinnedReportsCollection.fetch();
      this.SET_PINNED_REPORTS(items);
    } catch (error) {
      console.error('Failed to load pinned reports:', error);
      this.SET_PINNED_REPORTS([]);
    }
  }

  @Action
  async togglePinReport(payload: { id: number; title: string }):
    Promise<{ pinned: boolean; capped: boolean; error: boolean }> {
    const snapshot = [...this.pinnedReports];
    const exists = snapshot.some((r) => r.id === payload.id);

    if (!exists && snapshot.length >= MAX_PINNED_REPORTS) {
      return { pinned: false, capped: true, error: false };
    }

    let next: PinnedReport[];
    if (exists) {
      next = snapshot.filter((r) => r.id !== payload.id);
    } else {
      const item: PinnedReport = { id: payload.id, title: payload.title, pinnedAt: new Date().toISOString() };
      next = [item, ...snapshot].slice(0, MAX_PINNED_REPORTS);
    }

    this.SET_PINNED_REPORTS(next); // optimistic
    try {
      await pinnedReportsCollection.save(next);
      return { pinned: !exists, capped: false, error: false };
    } catch (error) {
      console.error('Failed to save pinned reports:', error);
      this.SET_PINNED_REPORTS(snapshot); // rollback
      return { pinned: false, capped: false, error: true };
    }
  }

  @Action
  async unpinReport(id: number): Promise<{ error: boolean }> {
    const snapshot = [...this.pinnedReports];
    const next = snapshot.filter((r) => r.id !== id);
    this.SET_PINNED_REPORTS(next); // optimistic
    try {
      await pinnedReportsCollection.save(next);
      return { error: false };
    } catch (error) {
      console.error('Failed to unpin report:', error);
      this.SET_PINNED_REPORTS(snapshot); // rollback
      return { error: true };
    }
  }

  @Action
  async pruneMissing(existingIds: number[]): Promise<void> {
    const set = new Set(existingIds);
    const next = this.pinnedReports.filter((r) => set.has(r.id));
    if (next.length === this.pinnedReports.length) return;
    this.SET_PINNED_REPORTS(next);
    try {
      await pinnedReportsCollection.save(next);
    } catch (error) {
      console.error('Failed to prune pinned reports:', error);
    }
  }
}

export default getModule(PinnedReportsModule, store);
